import { useEffect, useState } from "react";
import { api, fmtCurrency, fmtDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Plus, Pencil, Trash2, Euro } from "lucide-react";
import { toast } from "sonner";

const COST_CATEGORIES = [
  ["freelancer", "Freelancer"], ["media_budget", "Media Budget"], ["software", "Software/Tools"],
  ["rent", "Miete"], ["personnel", "Personal"], ["tax_advisor", "Steuerberater"],
  ["insurance", "Versicherung"], ["hosting", "Hosting"], ["production", "Produktion"],
  ["travel", "Reisekosten"], ["commissions", "Provisionen"], ["subcontractor", "Subunternehmer"],
  ["other_fixed", "Sonstige Fixkosten"], ["other_variable", "Sonstige variable Kosten"],
];

const STATUS_COLORS = {
  open: "bg-blue-100 text-blue-700",
  partially_paid: "bg-amber-100 text-amber-700",
  paid: "bg-emerald-100 text-emerald-700",
  overdue: "bg-red-100 text-red-700",
};
const STATUS_LABELS = { open: "Offen", partially_paid: "Teilw. bezahlt", paid: "Bezahlt", overdue: "Überfällig" };

const empty = {
  supplier_name: "", invoice_number: "", invoice_date: "", due_date: "",
  service_period: "", net_amount: 0, vat_rate: 19, cost_category: "other_variable",
  project_id: "", status: "open", notes: "",
  discount_days: "", discount_pct: "",
};

export default function IncomingInvoicesPage() {
  const [items, setItems] = useState([]);
  const [projects, setProjects] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [payOpen, setPayOpen] = useState(null);
  const [payForm, setPayForm] = useState({ amount: 0, payment_date: "", account_id: "", notes: "" });

  const load = async () => {
    const [inv, pr, ac] = await Promise.all([
      api.get("/incoming-invoices"), api.get("/projects"), api.get("/accounts"),
    ]);
    setItems(inv.data); setProjects(pr.data); setAccounts(ac.data);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    const payload = {
      ...form,
      net_amount: Number(form.net_amount), vat_rate: Number(form.vat_rate),
      project_id: form.project_id || null,
      discount_days: form.discount_days === "" || form.discount_days === null ? null : Number(form.discount_days),
      discount_pct: form.discount_pct === "" || form.discount_pct === null ? null : Number(form.discount_pct),
    };
    try {
      if (editing) await api.patch(`/incoming-invoices/${editing.id}`, payload);
      else await api.post("/incoming-invoices", payload);
      toast.success(editing ? "Rechnung aktualisiert" : "Rechnung angelegt");
      setOpen(false); setEditing(null); setForm(empty); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
  };

  const del = async (id) => {
    if (!window.confirm("Rechnung wirklich löschen?")) return;
    await api.delete(`/incoming-invoices/${id}`); toast.success("Gelöscht"); load();
  };

  const addPayment = async () => {
    try {
      await api.post(`/incoming-invoices/${payOpen.id}/payments`, {
        amount: Number(payForm.amount), payment_date: payForm.payment_date,
        account_id: payForm.account_id || null, notes: payForm.notes,
      });
      toast.success("Zahlung erfasst");
      setPayOpen(null); setPayForm({ amount: 0, payment_date: "", account_id: "", notes: "" }); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
  };

  const totalOpen = items.filter(i => i.status !== "paid").reduce((s, i) => s + (i.gross_amount - i.paid_amount), 0);
  const totalPaid = items.reduce((s, i) => s + (i.paid_amount || 0), 0);

  return (
    <div className="space-y-6" data-testid="incoming-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-neutral-500 font-semibold">Eingangsrechnungen</div>
          <h1 className="font-display text-3xl font-semibold tracking-tight mt-1">Lieferanten & Freelancer</h1>
        </div>
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditing(null); setForm(empty); } }}>
          <DialogTrigger asChild><Button data-testid="add-incoming-btn"><Plus className="h-4 w-4 mr-2" />Neue Rechnung</Button></DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader><DialogTitle>{editing ? "Rechnung bearbeiten" : "Neue Eingangsrechnung"}</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-4 max-h-[70vh] overflow-y-auto pr-2">
              <div className="col-span-2"><Label>Lieferant / Freelancer</Label><Input value={form.supplier_name} onChange={(e) => setForm({ ...form, supplier_name: e.target.value })} data-testid="in-inv-supplier" /></div>
              <div><Label>Rechnungsnummer</Label><Input value={form.invoice_number} onChange={(e) => setForm({ ...form, invoice_number: e.target.value })} /></div>
              <div>
                <Label>Kategorie</Label>
                <Select value={form.cost_category} onValueChange={(v) => setForm({ ...form, cost_category: v })}>
                  <SelectTrigger data-testid="in-inv-category"><SelectValue /></SelectTrigger>
                  <SelectContent>{COST_CATEGORIES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Rechnungsdatum</Label><Input type="date" value={form.invoice_date} onChange={(e) => setForm({ ...form, invoice_date: e.target.value })} /></div>
              <div><Label>Fällig am</Label><Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} /></div>
              <div><Label>Netto (€)</Label><Input type="number" step="0.01" value={form.net_amount} onChange={(e) => setForm({ ...form, net_amount: e.target.value })} data-testid="in-inv-net" /></div>
              <div><Label>USt-Satz %</Label><Input type="number" value={form.vat_rate} onChange={(e) => setForm({ ...form, vat_rate: e.target.value })} /></div>
              <div>
                <Label>Projektbezug</Label>
                <Select value={form.project_id || "none"} onValueChange={(v) => setForm({ ...form, project_id: v === "none" ? "" : v })}>
                  <SelectTrigger><SelectValue placeholder="Optional" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— Ohne Projekt —</SelectItem>
                    {projects.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(STATUS_LABELS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="col-span-2"><Label>Notizen</Label><Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
              <div className="col-span-2 pt-2 border-t border-neutral-200">
                <div className="text-[11px] uppercase tracking-[0.18em] text-neutral-500 font-semibold mb-2">Skonto (optional)</div>
              </div>
              <div><Label>Skontofrist (Tage)</Label><Input type="number" placeholder="z.B. 14" value={form.discount_days ?? ""} onChange={(e) => setForm({ ...form, discount_days: e.target.value })} data-testid="in-inv-discount-days" /></div>
              <div><Label>Skontosatz (%)</Label><Input type="number" step="0.01" placeholder="z.B. 2" value={form.discount_pct ?? ""} onChange={(e) => setForm({ ...form, discount_pct: e.target.value })} data-testid="in-inv-discount-pct" /></div>
            </div>
            <DialogFooter><Button onClick={save} data-testid="in-inv-save">{editing ? "Speichern" : "Anlegen"}</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
        <Stat label="Offene Verbindlichkeiten" value={fmtCurrency(totalOpen)} />
        <Stat label="Bereits bezahlt" value={fmtCurrency(totalPaid)} />
        <Stat label="Anzahl Rechnungen" value={items.length} />
      </div>

      <div className="rounded-xl border border-neutral-200 bg-white">
        <Table data-testid="in-inv-table">
          <TableHeader>
            <TableRow>
              <TableHead>Lieferant</TableHead><TableHead>Nr.</TableHead><TableHead>Kategorie</TableHead>
              <TableHead>Fällig</TableHead><TableHead className="text-right">Brutto</TableHead>
              <TableHead className="text-right">Bezahlt</TableHead><TableHead>Status</TableHead><TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((inv) => (
              <TableRow key={inv.id} className="table-row-hover" data-testid={`in-inv-row-${inv.id}`}>
                <TableCell className="font-medium">{inv.supplier_name}{inv.project && <div className="text-xs text-neutral-500">→ {inv.project.name}</div>}</TableCell>
                <TableCell className="text-sm">{inv.invoice_number}</TableCell>
                <TableCell className="text-sm">{COST_CATEGORIES.find(c => c[0] === inv.cost_category)?.[1]}</TableCell>
                <TableCell className="text-sm text-neutral-600">{fmtDate(inv.due_date)}</TableCell>
                <TableCell className="text-right tabular-nums">{fmtCurrency(inv.gross_amount)}</TableCell>
                <TableCell className="text-right tabular-nums">{fmtCurrency(inv.paid_amount)}</TableCell>
                <TableCell><Badge className={STATUS_COLORS[inv.status]} variant="secondary">{STATUS_LABELS[inv.status]}</Badge></TableCell>
                <TableCell className="text-right">
                  {inv.status !== "paid" && <Button variant="ghost" size="icon" onClick={() => { setPayOpen(inv); setPayForm({ amount: inv.gross_amount - inv.paid_amount, payment_date: new Date().toISOString().slice(0, 10), account_id: "", notes: "" }); }} data-testid={`in-inv-pay-${inv.id}`}><Euro className="h-4 w-4" /></Button>}
                  <Button variant="ghost" size="icon" onClick={() => { setEditing(inv); setForm({ ...empty, ...inv, project_id: inv.project_id || "" }); setOpen(true); }} data-testid={`in-inv-edit-${inv.id}`}><Pencil className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="icon" onClick={() => del(inv.id)} data-testid={`in-inv-delete-${inv.id}`}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                </TableCell>
              </TableRow>
            ))}
            {items.length === 0 && <TableRow><TableCell colSpan={8} className="text-center text-neutral-500 py-12">Noch keine Rechnungen</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!payOpen} onOpenChange={(v) => { if (!v) setPayOpen(null); }}>
        <DialogContent>
          <DialogHeader><DialogTitle>Zahlung erfassen — {payOpen?.supplier_name}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Betrag (€)</Label><Input type="number" step="0.01" value={payForm.amount} onChange={(e) => setPayForm({ ...payForm, amount: e.target.value })} data-testid="in-payment-amount" /></div>
            <div><Label>Zahlungsdatum</Label><Input type="date" value={payForm.payment_date} onChange={(e) => setPayForm({ ...payForm, payment_date: e.target.value })} /></div>
            <div>
              <Label>Konto</Label>
              <Select value={payForm.account_id || "none"} onValueChange={(v) => setPayForm({ ...payForm, account_id: v === "none" ? "" : v })}>
                <SelectTrigger><SelectValue placeholder="Konto wählen" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— Kein Konto —</SelectItem>
                  {accounts.map(a => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Notiz</Label><Input value={payForm.notes} onChange={(e) => setPayForm({ ...payForm, notes: e.target.value })} /></div>
          </div>
          <DialogFooter><Button onClick={addPayment} data-testid="in-payment-save">Zahlung speichern</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-xl border border-neutral-200 bg-white p-5">
      <div className="text-[11px] uppercase tracking-[0.18em] text-neutral-500 font-semibold">{label}</div>
      <div className="font-display text-2xl font-semibold mt-1">{value}</div>
    </div>
  );
}
